import React, { useEffect } from 'react';
import { Alert, AlertTitle, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@store/index';
import { selectNotifications, removeNotification } from '@features/ui/uiSlice';

const NotificationManager: React.FC = () => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="notification-stack"
      sx={{
        position: 'fixed',
        top: theme => theme.spacing(2),
        right: theme => theme.spacing(2),
        left: 'auto',
        bottom: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'flex-end',
        zIndex: theme => theme.zIndex.snackbar,
        pointerEvents: 'none',
      }}
    >
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={() => dispatch(removeNotification(notification.id))}
        />
      ))}
    </Box>
  );
};

interface NotificationItemProps {
  notification: {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
  };
  onClose: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClose }) => {
  const { t } = useTranslation();

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (notification.duration) {
      const timer = setTimeout(() => {
        handleClose();
      }, notification.duration);

      return () => clearTimeout(timer);
    }
    return;
  }, [notification.duration, handleClose]);

  return (
    <Alert
      severity={notification.type}
      onClose={handleClose}
      sx={{
        minWidth: 300,
        maxWidth: 420,
        pointerEvents: 'auto',
      }}
    >
      <AlertTitle>{t(`notifications.${notification.type}`)}</AlertTitle>
      {notification.message}
    </Alert>
  );
};

export default NotificationManager;
